import { Service, ServiceSellPrice } from "@turizi/shared";
import { formatPrice, todayStart, formatDay } from "@/lib/format-utils";
import { getServiceSellPrice } from "@/lib/models-utils";
import { useTranslation } from "react-i18next";
import { Checkbox, Button, Popover, Select, Switch, Tooltip } from "@mantine/core";
import { IconWorld, IconInfinity, IconEyeClosed, IconAdjustmentsHorizontal, IconRefresh, IconArrowsSort, IconSortAscending, IconSortDescending } from "@tabler/icons-react";
import { SERVICES_PATH } from "@/lib/navigation";
import { Link } from "react-router-dom";
import { ServiceTitle, ServiceTitleStatic } from "@/components/services/service-title";
import { TagIcon } from "@/lib/tag-utils";
import { useState, useMemo } from "react";
import BulkServiceEditModal from "@/components/service-tables/bulk-service-edit-modal";
// import ServicesShareModal from "@/components/services-share-modal";

interface Props {
    services: Service[];
    rawServices?: Service[];
    selectedCategory?: string | null;
    enableBulkActions?: boolean;
    selectedServices?: string[];
    toggleServiceSelection?: (id: string) => void;
    onSelectService?: (service: Service) => void;
    selectAll?: () => void;
    start_day?: string;
    end_day?: string;
    paxCount?: number;
}

type SortField = 'id' | 'name' | 'price_amount' | 'price_start_day' | 'price_end_day' | 'p_rank' | 's_rank';
type SortOrder = 'asc' | 'desc';

type TableColumnKey =
    | 'price'
    | 'price_updated'
    | 'min_max_pax'
    | 'limits'
    | 'status'
    | 'fav'
    | 'p_rank'
    | 's_rank';

const STORAGE_KEY_VISIBLE_COLUMNS = 'services_table_visible_columns';
const STORAGE_KEY_DETAILED_TITLE = 'services_table_detailed_title';

const DEFAULT_VISIBLE_COLUMNS: Record<TableColumnKey, boolean> = {
    price: true,
    price_updated: true,
    min_max_pax: true,
    limits: true,
    status: true,
    fav: true,
    p_rank: true,
    s_rank: true,
};

function getDisplayPrices(service: Service): ServiceSellPrice[] {
    if (!service.sell_prices || service.sell_prices.length === 0) return [];

    const sorted = [...service.sell_prices].sort((a, b) => {
        const dayA = a?.start_day || '';
        const dayB = b?.start_day || '';
        return dayA.localeCompare(dayB);
    });

    const unique: ServiceSellPrice[] = [];
    const seen = new Set<string>();

    for (const p of sorted) {
        if (p.default_amount === null || p.default_amount === undefined) continue;
        const key = `${p.default_amount}_${p.default_currency || ''}_${p.start_day || ''}_${p.end_day || ''}`;
        if (!seen.has(key)) {
            seen.add(key);
            unique.push(p);
        }
    }

    return unique.slice(0, 2);
}

export default function ServicesTable({
    services,
    rawServices,
    enableBulkActions = false,
    selectedServices = [],
    toggleServiceSelection,
    onSelectService,
    start_day = todayStart(),
    paxCount = 0,
    selectAll
}: Props) {
    const { t } = useTranslation();
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [showDetailedTitle, setShowDetailedTitle] = useState<boolean>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY_DETAILED_TITLE);
            return stored !== null ? stored === 'true' : false;
        } catch {
            return false;
        }
    });

    const handleToggleDetailedTitle = (checked: boolean) => {
        setShowDetailedTitle(checked);
        try {
            localStorage.setItem(STORAGE_KEY_DETAILED_TITLE, String(checked));
        } catch {
            // ignore
        }
    };
    const [visibleColumns, setVisibleColumns] = useState<Record<TableColumnKey, boolean>>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY_VISIBLE_COLUMNS);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed && typeof parsed === 'object') {
                    return {
                        ...DEFAULT_VISIBLE_COLUMNS,
                        ...parsed,
                    };
                }
            }
        } catch {
            // fallback
        }
        return DEFAULT_VISIBLE_COLUMNS;
    });

    const servicePool = rawServices || services;
    const selectedServicesList = useMemo(() => {
        if (!enableBulkActions || selectedServices.length === 0) return [];
        return servicePool.filter(s => selectedServices.includes(String(s.id)));
    }, [enableBulkActions, selectedServices, servicePool]);

    const toggleColumn = (key: TableColumnKey, visible: boolean) => {
        setVisibleColumns(prev => {
            const nextState = {
                ...prev,
                [key]: visible,
            };
            try {
                localStorage.setItem(STORAGE_KEY_VISIBLE_COLUMNS, JSON.stringify(nextState));
            } catch {
                // ignore
            }
            return nextState;
        });
    };

    const columnDefinitions: { key: TableColumnKey; label: string }[] = [
        { key: 'price', label: t('price') },
        { key: 'price_updated', label: t('price_updated') },
        { key: 'min_max_pax', label: t('min_max_pax') },
        { key: 'limits', label: t('limits') },
        { key: 'status', label: t('status') },
        { key: 'fav', label: 'Fav' },
        { key: 'p_rank', label: 'P. Rank' },
        { key: 's_rank', label: 'S. Rank' },
    ];

    const sortedServices = useMemo(() => {
        if (!sortField) return services;

        return [...services].sort((a, b) => {
            if (sortField === 'name') {
                const titleA = ServiceTitleStatic(a) || '';
                const titleB = ServiceTitleStatic(b) || '';
                const comp = titleA.localeCompare(titleB, undefined, { numeric: true, sensitivity: 'base' });
                return sortOrder === 'asc' ? comp : -comp;
            }

            const pricesA = getDisplayPrices(a);
            const pricesB = getDisplayPrices(b);
            const priceA = pricesA[0] || getServiceSellPrice(a, start_day)?.[0];
            const priceB = pricesB[0] || getServiceSellPrice(b, start_day)?.[0];

            let valA: any = '';
            let valB: any = '';

            if (sortField === 'id') {
                valA = Number(a.id) || 0;
                valB = Number(b.id) || 0;
            } else if (sortField === 'p_rank') {
                const rankA = a?.providers?.[0]?.tenant_business?.rank;
                const rankB = b?.providers?.[0]?.tenant_business?.rank;
                valA = rankA !== null && rankA !== undefined ? Number(rankA) : Infinity;
                valB = rankB !== null && rankB !== undefined ? Number(rankB) : Infinity;
            } else if (sortField === 's_rank') {
                const rankA = a?.rank;
                const rankB = b?.rank;
                valA = rankA !== null && rankA !== undefined ? Number(rankA) : Infinity;
                valB = rankB !== null && rankB !== undefined ? Number(rankB) : Infinity;
            } else if (sortField === 'price_amount') {
                valA = priceA?.default_amount ?? -Infinity;
                valB = priceB?.default_amount ?? -Infinity;
            } else if (sortField === 'price_start_day') {
                valA = priceA?.start_day || '';
                valB = priceB?.start_day || '';
            } else if (sortField === 'price_end_day') {
                valA = priceA?.end_day || '';
                valB = priceB?.end_day || '';
            }

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;

            if (sortField === 'price_amount') {
                const currA = (priceA?.default_currency || '').toLowerCase();
                const currB = (priceB?.default_currency || '').toLowerCase();
                const currComp = currA.localeCompare(currB);
                if (currComp !== 0) return sortOrder === 'asc' ? currComp : -currComp;
            }

            const titleA = ServiceTitleStatic(a) || '';
            const titleB = ServiceTitleStatic(b) || '';
            return titleA.localeCompare(titleB);
        });
    }, [services, sortField, sortOrder, start_day]);

    const handleRowClick = (e: React.MouseEvent, service: Service) => {
        if (onSelectService) {
            e.preventDefault();
            onSelectService(service);
        }
    };

    return (
        <div className="space-y-2 w-full max-w-full">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                {enableBulkActions ? (
                    <div className="flex items-center gap-2 flex-wrap">
                        <BulkServiceEditModal
                            services={selectedServicesList}
                            target={(
                                <Button size="xs" radius="md" color="black" disabled={selectedServices.length === 0}>
                                    {t('edit')} {selectedServices.length > 0 ? `(${selectedServices.length})` : ''}
                                </Button>
                            )}
                        />
                    </div>
                ) : <div />}

                <div className="flex items-center gap-2 flex-wrap">
                    {/* Sort selector */}
                    <Select
                        size="xs"
                        radius="md"
                        value={sortField}
                        onChange={(val) => val && setSortField(val as SortField)}
                        data={[
                            { value: 'id', label: t('service_id_sort') },
                            { value: 'name', label: t('name') },
                            { value: 'price_amount', label: t('price_amount_currency') },
                            { value: 'price_start_day', label: t('price_start_day') },
                            { value: 'price_end_day', label: t('price_end_day') },
                            { value: 'p_rank', label: 'P. Rank' },
                            { value: 's_rank', label: 'S. Rank' },
                        ]}
                        leftSection={<IconArrowsSort size={14} />}
                        allowDeselect={false}
                        className="w-36 sm:w-48"
                    />

                    {/* Sort order toggle button */}
                    <Button
                        variant="subtle"
                        color="gray"
                        size="xs"
                        radius="md"
                        onClick={() => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                        title={sortOrder === 'asc' ? t('ascending') : t('descending')}
                    >
                        {sortOrder === 'asc' ? <IconSortAscending size={16} /> : <IconSortDescending size={16} />}
                    </Button>

                    {/* Columns selector */}
                    <Popover position="bottom-end" shadow="md" radius="md" withArrow>
                        <Popover.Target>
                            <Button
                                variant="subtle"
                                color="gray"
                                size="xs"
                                radius="md"
                                leftSection={<IconAdjustmentsHorizontal size={14} />}
                            >
                                {t('columns')}
                            </Button>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <div className="space-y-2 p-1 min-w-[160px]">
                                <div className="text-xs font-bold text-gray-700 pb-1 border-b border-gray-100">
                                    {t('visible_columns')}
                                </div>
                                <div className="space-y-1.5 max-h-[220px] overflow-y-auto">
                                    {columnDefinitions.map(col => (
                                        <Checkbox
                                            key={col.key}
                                            size="xs"
                                            color="dark"
                                            label={col.label}
                                            checked={Boolean(visibleColumns[col.key])}
                                            onChange={(e) => toggleColumn(col.key, e.currentTarget.checked)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Popover.Dropdown>
                    </Popover>

                    {/* Detailed Title View Switch */}
                    <Tooltip label={t('detailed_title')}>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <Switch
                                size="xs"
                                color="black"
                                checked={showDetailedTitle}
                                onChange={(event) => handleToggleDetailedTitle(event.currentTarget.checked)}
                                label={<span className="text-xs font-medium text-gray-700 select-none whitespace-nowrap">{t('detailed_title')}</span>}
                            />
                        </div>
                    </Tooltip>
                </div>
            </div>

            <div className="subtle-table-sm w-full overflow-x-auto">
                <table className="w-full min-w-[760px] border-collapse">
                    <thead>
                        <tr>
                            {enableBulkActions && (
                                <th className="w-8 px-2 py-2 bg-gray-100">
                                    <Checkbox
                                        color="black"
                                        size="sm"
                                        checked={selectedServices.length === services.length && services.length > 0}
                                        onChange={() => selectAll?.()}
                                    />
                                </th>
                            )}
                            <th className="number w-10 px-2">#</th>
                            <th className="min-w-[200px] text-left px-2">{t('name')}</th>
                            {visibleColumns.price && <th className="min-w-[220px] text-right px-2">{t('price')}</th>}
                            {visibleColumns.price_updated && <th className="w-24 text-left px-2"><IconRefresh size={12} /></th>}
                            {visibleColumns.min_max_pax && <th className="w-20 text-center px-2">{t('min_max_pax')}</th>}
                            {visibleColumns.limits && <th className="w-16 text-center px-2">{t('limits')}</th>}
                            {visibleColumns.status && <th className="w-24 text-center px-2">{t('status')}</th>}
                            {visibleColumns.fav && <th className="w-10 text-center px-2">fav</th>}
                            {visibleColumns.p_rank && <th className="number w-16 px-2">P. Rank</th>}
                            {visibleColumns.s_rank && <th className="number w-16 px-2">S. Rank</th>}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {sortedServices.map(service => {
                            const displayPrices = getDisplayPrices(service);
                            const daily_available = service.availability?.daily_available;
                            const isSelected = selectedServices.includes(String(service.id));

                            return (
                                <tr
                                    key={service.id}
                                    className={`
                                            clickable-row cursor-pointer 
                                            ${isSelected ? 'row-selected selected' : ''}
                                        `}
                                >
                                    {enableBulkActions && (
                                        <td className="relative z-20 px-2" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                color="black"
                                                size="sm"
                                                checked={isSelected}
                                                onChange={() => toggleServiceSelection?.(String(service.id))}
                                            />
                                        </td>
                                    )}

                                    {/* ID Column */}
                                    <td className="number font-medium px-2">
                                        <div className="text-xs text-gray-400">
                                            <div>#{service.id}</div>
                                        </div>
                                    </td>

                                    {/* Title Column */}
                                    <td className="font-medium text-gray-900 whitespace-normal text-wrap break-words min-w-[200px] px-2">
                                        <Link
                                            to={`${SERVICES_PATH}/${service.id}`}
                                            className="main-link"
                                            onClick={(e) => handleRowClick(e, service)}
                                        />
                                        <div className="flex items-start gap-1.5 py-0.5 min-w-0">
                                            <TagIcon name={service.category} size="xs" hideText className="shrink-0 mt-0.5" />
                                            {showDetailedTitle ? (
                                                <ServiceTitle service={service} />
                                            ) : (
                                                <span className="text-xs font-medium text-gray-800 whitespace-normal text-wrap break-words">
                                                    {ServiceTitleStatic(service)}
                                                </span>
                                            )}
                                        </div>
                                    </td>

                                    {/* Combined Price Column (Amount, Currency, Dates) */}
                                    {visibleColumns.price && (
                                        <td className="text-right whitespace-nowrap px-2">
                                            {displayPrices.length > 0 ? (
                                                <div className="flex flex-col gap-1 items-end justify-end">
                                                    {displayPrices.map((p, idx) => {
                                                        const startStr = formatDay(p.start_day, '');
                                                        const endStr = formatDay(p.end_day, '');
                                                        const hasDates = Boolean(startStr || endStr);

                                                        return (
                                                            <div key={p.id || idx} className="flex items-baseline justify-end gap-1 whitespace-nowrap leading-tight">
                                                                <span className="font-semibold text-gray-900 text-xs">
                                                                    ${formatPrice(p.default_amount)}
                                                                </span>
                                                                <span className="text-[10px] font-semibold text-gray-500">
                                                                    {p.default_currency || '**'}
                                                                </span>
                                                                {hasDates && (
                                                                    <span className="text-[11px] text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md font-normal ml-1">
                                                                        {startStr && endStr ? `${startStr} - ${endStr}` : startStr || endStr}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="text-gray-300">-</span>
                                            )}
                                        </td>
                                    )}

                                    {/* Price Updated Column */}
                                    {visibleColumns.price_updated && (
                                        <td className="whitespace-nowrap px-2">
                                            {displayPrices.length > 0 ? (
                                                <div className="flex flex-col gap-1">
                                                    {displayPrices.map((p, idx) => {
                                                        const updatedStr = formatDay(p.updated_at || p.created_at);
                                                        return (
                                                            <div key={p.id || idx} className="text-xs text-gray-500 whitespace-nowrap">
                                                                {updatedStr}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400 whitespace-nowrap">
                                                    {service.updated_at ? formatDay(service.updated_at) : '-'}
                                                </span>
                                            )}
                                        </td>
                                    )}

                                    {/* Min/Max Pax Column */}
                                    {visibleColumns.min_max_pax && (
                                        <td className="text-center whitespace-nowrap px-2">
                                            {service.min_pax_per_booking || '0'} / {service.max_pax_per_booking || '∞'}
                                        </td>
                                    )}

                                    {/* Limits / Availability Column */}
                                    {visibleColumns.limits && (
                                        <td className="text-center whitespace-nowrap px-2">
                                            <div className="flex flex-col gap-0.5 items-center justify-center">
                                                {daily_available !== undefined && daily_available !== null ? (
                                                    <span className={`text-xs font-semibold ${daily_available < paxCount ? 'text-red-600' : 'text-green-600'}`}>
                                                        {daily_available}
                                                    </span>
                                                ) : (
                                                    <span>
                                                        <IconInfinity size={12} stroke={2} />
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    )}

                                    {/* Published / Status Column */}
                                    {visibleColumns.status && (
                                        <td className="text-center whitespace-nowrap px-2">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xxs font-medium ${service.publish_price
                                                ? "bg-green-50 text-green-700 border border-green-100"
                                                : "bg-gray-50 text-gray-700 border border-gray-100"
                                                }`}>
                                                {service.publish_price ? <IconWorld size={12} stroke={2} /> : <IconEyeClosed size={12} stroke={2} />}
                                                <span>{service.publish_price ? t('published') : t('private')}</span>
                                            </span>
                                        </td>
                                    )}

                                    {/* Favorite Column */}
                                    {visibleColumns.fav && (
                                        <td className="text-center whitespace-nowrap px-2">
                                            <div>{service.is_favorite ? '⭐' : ''}</div>
                                        </td>
                                    )}

                                    {/* Provider Rank Column */}
                                    {visibleColumns.p_rank && (
                                        <td className="number whitespace-nowrap px-2">
                                            <div className="text-gray-400">
                                                {service?.providers?.[0]?.tenant_business?.rank}
                                            </div>
                                        </td>
                                    )}

                                    {/* Service Rank Column */}
                                    {visibleColumns.s_rank && (
                                        <td className="number whitespace-nowrap px-2">
                                            <div className="text-gray-400 number">
                                                {service?.rank}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
